import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import isNil from "lodash/isNil";
import DoneIcon from "@mui/icons-material/Done";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";

import { MetaType } from "core/enums";
import { MetaDataEditField } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaDataEditField";
import { LocaleContext } from "contexts/LocaleContext";

export function EditableMetaField({
  attribute,
  metaType,
  onAttributeValueChanged
}) {
  const { formatShort } = useContext(LocaleContext);
  const [attributeFieldValue, setAttributeFieldValue] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isValueEditing, setIsValueEditing] = useState(false);

  useEffect(() => {
    setAttributeFieldValue(attribute?.value ?? "");
  }, [attribute?.value]);

  const handleFieldClicked = () => {
    setIsValueEditing(true);
  };

  const handleUpdateAttribute = () => {
    setIsValueEditing(false);
    let parsedAttributeValue = attributeFieldValue;
    if (metaType.data_type.id === MetaType.NUMBER) {
      parsedAttributeValue = parseFloat(attributeFieldValue);
    }
    onAttributeValueChanged({ ...attribute, value: parsedAttributeValue });
  };

  const getAttributeValue = attribute => {
    let attributeFieldValue = attribute?.value;

    if (metaType?.data_type?.id === MetaType.DATE) {
      attributeFieldValue = formatShort(new Date(attributeFieldValue * 1000));
    }

    if (
      (attributeFieldValue !== 0 && isNil(attributeFieldValue)) ||
      (attributeFieldValue.trim && attributeFieldValue.trim() === "")
    ) {
      return "Click to set value...";
    } else return attributeFieldValue;
  };

  const EditInputAdornment = () => (
    <IconButton
      size="small"
      onClick={handleUpdateAttribute}
      disabled={Boolean(validationError)}
      edge="end"
    >
      <DoneIcon />
    </IconButton>
  );

  return isValueEditing ? (
    <MetaDataWrapper>
      <MetaDataEditField
        attributeFieldValue={attributeFieldValue}
        attributeDataType={metaType?.data_type}
        setAttributeFieldValue={setAttributeFieldValue}
        categoricalValues={metaType?.data_value_details}
        setValidationError={setValidationError}
        enableLabel={false}
        size="small"
        inputAdornment={<EditInputAdornment />}
      />
    </MetaDataWrapper>
  ) : (
    <MetaDataWrapper
      onClick={handleFieldClicked}
      role="button"
      aria-roledescription="clickable component to edit meta field value"
    >
      {getAttributeValue(attribute)}
    </MetaDataWrapper>
  );
}

const MetaDataWrapper = styled("div")`
  cursor: pointer;
`;

EditableMetaField.propTypes = {
  attribute: PropTypes.object.isRequired,
  metaType: PropTypes.object.isRequired,
  onAttributeValueChanged: PropTypes.func.isRequired
};
